import React, { ReactElement, useCallback } from 'react';
import { Modal } from '../Modal';
import { RoleSelectionModal } from '../RoleSelectionModal';
import { OrganizationModalContent, ModalCloseButton } from './components';
import { useOrganizationModalState } from './hooks';

export interface OrganizationEventsModalProps {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  organizationName?: string;
  organizationLogoUrl?: string;
  schoolSlug?: string;
  onMembershipChange?: () => void;
}

export const OrganizationEventsModal = ({
  open,
  onClose,
  organizationId: initialOrganizationId,
  organizationName,
  organizationLogoUrl,
  schoolSlug,
  onMembershipChange,
}: OrganizationEventsModalProps): ReactElement => {
  const {
    isLeadersExpanded,
    setIsLeadersExpanded,
    selectedOrganizationId,
    isRoleModalOpen,
    setIsRoleModalOpen,
    isEditMode,
    availableOrganizations,
    orgLeaders,
    events,
    isLoading,
    isFollowed,
    myLeaderRequest,
    isEligibleForLeaderRequest,
    hasPendingRequest,
    displayName,
    displayLogoUrl,
    roleModalInitialRole,
    showFollowButton,
    handleOrganizationChange,
    handleOpenRoleModal,
    handleEditRequest,
    handleRequestLeaderAccess,
    handleUpdateLeaderRequest,
    handleCancelLeaderRequest,
    handleAcceptInvite,
    handleDeclineInvite,
    handleLeaveMyRole,
    isAcceptedLeader,
    membershipStatus,
    isSameSchool,
    handleRequestMembership,
    handleCancelMembershipRequest,
    handleLeaveOrganization,
    schoolTermsUrl,
    schoolTermsDisplayName,
    organizationSchoolName,
  } = useOrganizationModalState({
    open,
    initialOrganizationId,
    organizationName,
    organizationLogoUrl,
    schoolSlug,
    onMembershipChange,
  });

  const handleCloseRoleModal = useCallback(() => {
    setIsRoleModalOpen(false);
  }, [setIsRoleModalOpen]);

  const handleRoleSubmit = useCallback(
    async (role: string) => {
      await (isEditMode ? handleUpdateLeaderRequest(role) : handleRequestLeaderAccess(role));
    },
    [isEditMode, handleUpdateLeaderRequest, handleRequestLeaderAccess],
  );

  return (
    <>
      <Modal
        open={open && !isRoleModalOpen}
        onClose={onClose}
        title="Campus Organization"
        headerStyle={{ textAlign: 'left', justifyContent: 'start' }}
        contentStyle={{
          width: { xs: '100%', sm: '100%', md: 600 },
          minWidth: { xs: '100%', sm: '100%', md: 600 },
          maxWidth: { xs: '100%', sm: '100%', md: 600 },
          maxHeight: { xs: '90vh', md: '80vh' },
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
      <OrganizationModalContent
        availableOrganizations={availableOrganizations}
        selectedOrganizationId={selectedOrganizationId}
        displayName={displayName}
        displayLogoUrl={displayLogoUrl}
        onOrganizationChange={handleOrganizationChange}
        showFollowButton={showFollowButton}
        isLoading={isLoading}
        events={events}
        isFollowed={isFollowed}
        onClose={onClose}
        orgLeaders={orgLeaders}
        isLeadersExpanded={isLeadersExpanded}
        onToggleLeaders={() => setIsLeadersExpanded(!isLeadersExpanded)}
        isEligibleForLeaderRequest={isEligibleForLeaderRequest}
        hasPendingRequest={hasPendingRequest}
        myLeaderRequest={myLeaderRequest}
        organizationName={displayName}
        onOpenRoleModal={handleOpenRoleModal}
        onEditRequest={handleEditRequest}
        onCancelRequest={handleCancelLeaderRequest}
        onAcceptInvite={handleAcceptInvite}
        onDeclineInvite={handleDeclineInvite}
        schoolTermsUrl={schoolTermsUrl}
        schoolTermsDisplayName={schoolTermsDisplayName}
        membershipStatus={membershipStatus}
        isSameSchool={isSameSchool}
        onRequestMembership={handleRequestMembership}
        onCancelMembershipRequest={handleCancelMembershipRequest}
        onLeaveOrganization={handleLeaveOrganization}
        isAcceptedLeader={isAcceptedLeader}
        onLeaveMyRole={handleLeaveMyRole}
        schoolName={organizationSchoolName}
      />

      <ModalCloseButton onClose={onClose} />
    </Modal>

      <RoleSelectionModal
        open={isRoleModalOpen}
        onClose={handleCloseRoleModal}
        onSubmit={handleRoleSubmit}
        organizationName={displayName}
        organizationLogoUrl={displayLogoUrl}
        initialRole={roleModalInitialRole}
        isEditMode={isEditMode}
        schoolTermsUrl={schoolTermsUrl}
        schoolTermsDisplayName={schoolTermsDisplayName}
      />
    </>
  );
};
